import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Website-manager dashboard aggregates - per-surface draft/live counts,
 * the "needs attention" list (live rows with missing SEO / content fields)
 * and a cross-surface recently-edited feed.
 *
 * Read-only and role-scoped to the website roles; see
 * `GET /dashboard/website-health`.
 */

export const WebsiteSurfaceKeySchema = z.enum([
  'blog',
  'blog_category',
  'page',
  'destination_page',
  'category_page',
  'package',
]);
export type WebsiteSurfaceKey = z.infer<typeof WebsiteSurfaceKeySchema>;

export const WebsiteSurfaceCountsSchema = z.object({
  key: WebsiteSurfaceKeySchema,
  total: z.number().int().nonnegative(),
  /** Rows not on the public site yet (DRAFT). Blog categories report 0. */
  draft: z.number().int().nonnegative(),
  /** Rows on the public site (PUBLISHED, or ACTIVE for packages / categories). */
  live: z.number().int().nonnegative(),
});
export type WebsiteSurfaceCounts = z.infer<typeof WebsiteSurfaceCountsSchema>;

export const WebsiteIncompleteItemSchema = z.object({
  surface: WebsiteSurfaceKeySchema,
  id: UuidSchema,
  title: z.string(),
  slug: z.string(),
  /** Human field labels still to fill in - rendered as chips, worst row first. */
  missing: z.array(z.string()),
  updatedAt: IsoDateTimeSchema,
});
export type WebsiteIncompleteItem = z.infer<typeof WebsiteIncompleteItemSchema>;

export const WebsiteIncompleteCountsSchema = z.object({
  surface: WebsiteSurfaceKeySchema,
  /** Live rows missing at least one field - visible to Google right now. */
  live: z.number().int().nonnegative(),
  /** Drafts missing at least one field - expected, shown as a soft number. */
  draft: z.number().int().nonnegative(),
  /** Live rows missing SEO (meta title / description / OG image) specifically. */
  seoLive: z.number().int().nonnegative(),
  seoDraft: z.number().int().nonnegative(),
  /** Live rows missing content (hero / about / FAQs / sliders / excerpt). */
  contentLive: z.number().int().nonnegative(),
  contentDraft: z.number().int().nonnegative(),
});
export type WebsiteIncompleteCounts = z.infer<typeof WebsiteIncompleteCountsSchema>;

export const WebsiteRecentEditSchema = z.object({
  surface: WebsiteSurfaceKeySchema,
  id: UuidSchema,
  title: z.string(),
  slug: z.string(),
  status: z.string(),
  updatedAt: IsoDateTimeSchema,
  /** Raw id so a viewer can filter the feed down to their own edits. */
  updatedById: UuidSchema.nullable(),
  updatedByName: z.string().nullable(),
});
export type WebsiteRecentEdit = z.infer<typeof WebsiteRecentEditSchema>;

export const WebsiteHealthResponseSchema = z.object({
  /** One row per website surface, in the order the dashboard tiles render. */
  surfaces: z.array(WebsiteSurfaceCountsSchema),
  /** Incomplete totals per surface - the card header numbers. */
  incompleteCounts: z.array(WebsiteIncompleteCountsSchema),
  /** Preview rows for the card body. Live rows only; capped server-side. */
  incompleteItems: z.array(WebsiteIncompleteItemSchema),
  /** Newest edits across blogs / pages / destination + category pages. */
  recentlyEdited: z.array(WebsiteRecentEditSchema),
  generatedAt: IsoDateTimeSchema,
});
export type WebsiteHealthResponse = z.infer<typeof WebsiteHealthResponseSchema>;
